import { buildUrl } from "@payping/utilities-shared/url";
import { Button, Card, Col, Flex, Grid, Row, Space, Tag, Typography } from "@payping/web-components";
import React, { SVGProps } from "react";

import { CardItemDataType, ListCardProps } from "@payping/web-components/components/List/types";
import { useAppTranslation } from "@/utils/useAppTranslation";
import {
  allDocumentId,
  csharpGithubLinkId,
  invoiceDocumentId,
  laravelGithubLinkId,
  paymentDocumentId,
  phpGithubLinkId,
  postmanTemplateDownloadId,
  reportDocumentId,
  sdkGithubLinkId,
  webserviceHelpId,
  withdrawDocumentId,
} from "@/assets/googleTrackIds";
import { ArrowLeftOutlined, Faktor, Gozaresh, OpenBlank, Pardakht, Tasvie } from "@payping/icons";
import { URLS } from "@payping/utilities-shared/consts";

const Document: React.FC = () => {
  const { t } = useAppTranslation();
  const codeSampleSdkSrc = buildUrl(URLS.STORAGE_URL, "img/document/sample_sdk.png").toString();
  const codeSampleCSharpSrc = buildUrl(URLS.STORAGE_URL, "img/document/sample_csharp.png").toString();
  const codeSamplePhpSrc = buildUrl(URLS.STORAGE_URL, "img/document/sample_php.png").toString();
  const codeSampleLaravelSrc = buildUrl(URLS.STORAGE_URL, "img/document/sample_laravel.png").toString();
  const { lg } = Grid.useBreakpoint();

  // <SampleData Source>

  const sampleDataSource: ListCardProps["dataSource"] = [
    {
      key: "codeSampleSdkSrc",
      image: {
        type: "avatar",
        content: codeSampleSdkSrc,
        size: 54,
      },
      actions: [
        <a
          key={"codeSampleSdkSrc"}
          href={buildUrl(`https://${URLS.BASE_URL}`, "sdk").toString()}
          data-google-track={sdkGithubLinkId}
          target={"_blank"}
          rel="noreferrer"
        >
          <Space>
            {t("github")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
      descriptions: <Typography.Text>{t("ipg.document.codeSample.sdk")}</Typography.Text>,
    },
    {
      key: "codeSampleCSharpSrc",
      image: {
        type: "avatar",
        content: codeSampleCSharpSrc,
        size: 54,
      },
      actions: [
        <a
          key={"codeSampleSdkSrc"}
          href={
            "https://github.com/payping/Sample-OAuth-Code/blob/master/C%23/Sample-dotnet-4.5/PayPing.Ipg/Controllers/PayPingController.cs"
          }
          target={"_blank"}
          rel="noreferrer"
          data-google-track={csharpGithubLinkId}
        >
          <Space>
            {t("github")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
      descriptions: <Typography.Text>{t("ipg.document.codeSample.csharp")}</Typography.Text>,
    },
    {
      key: "codeSamplePhpSrc",
      image: { type: "avatar", content: codeSamplePhpSrc, size: 54 },
      actions: [
        <a
          key={"codeSampleCSharpSrc"}
          href={"https://github.com/payping/PHP-Basic-Example-Payment"}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={phpGithubLinkId}
        >
          <Space>
            {t("github")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
      descriptions: <Typography.Text>{t("ipg.document.codeSample.php")}</Typography.Text>,
    },
    {
      key: "codeSampleLaravelSrc",
      image: { type: "avatar", content: codeSampleLaravelSrc, size: 54 },
      actions: [
        <a
          key={"codeSampleCSharpSrc"}
          href={"https://github.com/payping/payping-php-client"}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={laravelGithubLinkId}
        >
          <Space>
            {t("github")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
      descriptions: <Typography.Text>{t("ipg.document.codeSample.laravel")}</Typography.Text>,
    },
  ];

  const cardSample1: CardItemDataType = sampleDataSource[0];
  const cardSample2: CardItemDataType = sampleDataSource[1];
  const cardSample3: CardItemDataType = sampleDataSource[2];
  const cardSample4: CardItemDataType = sampleDataSource[3];

  // </SampleData Source>

  // IconStandard For WebService Section
  type WebServiceIcon = (props: SVGProps<SVGSVGElement>) => JSX.Element;
  const webServicesIcon = (Icon: WebServiceIcon) => (
    <Flex justify={"center"} align={"center"} className={"tw-bg-color-bg-layout tw-p-4"}>
      <Icon width={54} height={54} className={"tw-border-none tw-bg-color-bg-layout"} color={"black"} />
    </Flex>
  );

  const webServicesTitle = (text: string) => (
    <Typography.Title level={5} className={"tw-font-medium"}>
      {text}
    </Typography.Title>
  );

  // <WebServicesData Source>
  const webServicesDataSource: ListCardProps["dataSource"] = [
    {
      key: "paymentDocument",
      image: {
        type: "icon",
        content: webServicesIcon(Pardakht),
      },
      title: {
        primary: webServicesTitle(t("document.payment")),
      },
      content: [
        <Space key={"paymentDocument"} size={"small"}>
          <Tag>{t("settlement.requiredWebServices.help")}</Tag>
          <Tag>Payment</Tag>
        </Space>,
      ],
      actions: [
        <a
          key={"paymentDocument"}
          href={`${URLS.DOCS_URL}/#tag/Payment`}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={paymentDocumentId}
        >
          <Space>
            {t("goto")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
    },
    {
      key: "reportDocument",
      image: { type: "icon", content: webServicesIcon(Gozaresh) },
      title: {
        primary: webServicesTitle(t("document.report")),
      },
      content: [
        <Space key={"reportDocument"} size={"small"}>
          <Tag>{t("settlement.requiredWebServices.help")}</Tag>
          <Tag>Report</Tag>
        </Space>,
      ],
      actions: [
        <a
          key={"reportDocument"}
          href={`${URLS.DOCS_URL}/#tag/Report`}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={reportDocumentId}
        >
          <Space>
            {t("goto")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
    },
    {
      key: "withdrawDocument",
      image: {
        type: "icon",
        content: webServicesIcon(Tasvie),
      },
      title: {
        primary: webServicesTitle(t("document.withdraw")),
      },
      content: [
        <Space key={"withdrawDocument"} size={"small"}>
          <Tag>{t("settlement.requiredWebServices.help")}</Tag>
          <Tag>Withdraw</Tag>
        </Space>,
      ],
      actions: [
        <a
          key={"withdrawDocument"}
          href={`${URLS.DOCS_URL}/#tag/Withdraw`}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={withdrawDocumentId}
        >
          <Space>
            {t("goto")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
    },
    {
      key: "invoiceDocument",
      image: {
        type: "icon",
        content: webServicesIcon(Faktor),
      },
      title: {
        primary: webServicesTitle(t("document.invoice")),
      },
      content: [
        <Space key={"invoiceDocument"} size={"small"}>
          <Tag>{t("settlement.requiredWebServices.help")}</Tag>
          <Tag>Invoice</Tag>
        </Space>,
      ],
      actions: [
        <a
          key={"invoiceDocument"}
          href={`${URLS.DOCS_URL}/#tag/Invoice`}
          target={"_blank"}
          rel="noreferrer"
          data-google-track={invoiceDocumentId}
        >
          <Space>
            {t("goto")}
            <OpenBlank width={10} />
          </Space>
        </a>,
      ],
    },
  ];

  const cardWebService1: CardItemDataType = webServicesDataSource[0];
  const cardWebService2: CardItemDataType = webServicesDataSource[1];
  const cardWebService3: CardItemDataType = webServicesDataSource[2];
  const cardWebService4: CardItemDataType = webServicesDataSource[3];

  // </WebServicesData Source>

  return (
    <>
      {/* <WebServices> */}
      <Row gutter={[0, 16]}>
        {/* WebService Title Col */}
        <Col span={24}>
          <Typography.Title level={4}>{t("webServices")}</Typography.Title>
        </Col>
        {/* WebService Cards Col */}
        <Col xs={24} lg={21}>
          <Flex
            className={!lg ? "tw--ml-4 tw-overflow-x-scroll" : "tw-overflow-x-scroll"}
            style={{
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            <Card data={cardWebService1} />
            <Card data={cardWebService2} />
            <Card data={cardWebService3} />
            <Card data={cardWebService4} />
          </Flex>
        </Col>
        {/* WebService More Col */}
        <Col xs={24} lg={3}>
          <div className={"tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center"}>
            <a href={URLS.DOCS_URL} target={"_blank"} rel="noreferrer" data-google-track={allDocumentId}>
              <Space>
                {t("document.title")}
                <ArrowLeftOutlined width={10} />
              </Space>
            </a>
          </div>
        </Col>
        {/* WebService Description Col */}
        <Col span={24}>
          <Space direction={"vertical"} size={"large"} className={"tw-w-full"}>
            <Typography.Text className={"line-height-4"}>{t("ipg.document.codeSample.postMan.desc")}</Typography.Text>
            <Space>
              <Button>
                <a
                  href={"https://statics.payping.ir/postman.json"}
                  className={"color--black"}
                  target={"_blank"}
                  rel="noreferrer"
                  data-google-track={postmanTemplateDownloadId}
                >
                  {t("ipg.document.codeSample.postMan.title")}
                </a>
              </Button>
              <a
                href={`${URLS.HELP_URL}/fa/category/api--qspohi/`}
                target={"_blank"}
                rel="noreferrer"
                data-google-track={webserviceHelpId}
              >
                {t("settlement.requiredWebServices.title")}
              </a>
            </Space>
          </Space>
        </Col>
      </Row>
      {/* </WebService> */}

      {/*  <Needed Resource> */}
      <Space direction={"vertical"} className={"tw-my-12 tw-w-full"}>
        <Row>
          <Col span={24}>
            <Typography.Title level={4}>{t("ipg.document.codeSample.title")}</Typography.Title>
          </Col>
          <Col span={24}>
            <Flex
              className={!lg ? "tw--ml-4 tw-overflow-x-scroll" : "tw-overflow-x-scroll"}
              style={{
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }}
            >
              <Card data={cardSample1} />
              <Card data={cardSample2} />
              <Card data={cardSample3} />
              <Card data={cardSample4} />
            </Flex>
          </Col>
        </Row>
      </Space>
      {/*  </Needed Resource> */}
    </>
  );
};

export default Document;
